import { noop } from "lodash";
import React, { MouseEvent } from "react";

import ACButtonGroup, { ACButtonGroupItem } from "ac-components/ACButtonGroup";
import ChevronLeftIcon from "ac-components/ac-icons/ChevronLeftIcon";
import ChevronRightIcon from "ac-components/ac-icons/ChevronRightIcon";

import "./ACPagination.less";

type Props = {
  size?: "sm" | "md";
  page: number;
  pageSize?: number;
  total: number;
  showEdges?: boolean;
  onChange: (current: number) => void;
};

export type { Props as ACPaginationProps };

function ACPagination({
  size = "md",
  page = 1,
  total = 10,
  pageSize = 10,
  showEdges = true,
  onChange = noop,
}: Props) {
  const totalPages = Math.ceil(total / pageSize);
  const rangeDiff = showEdges ? 1 : 3;
  const totalRangeButtons = rangeDiff * 2 + 1;
  let startRange = Math.max(1, page - rangeDiff);
  let endRange = Math.min(totalPages, startRange + totalRangeButtons - 1);
  if (
    totalPages > totalRangeButtons &&
    endRange - startRange < totalRangeButtons - 1
  ) {
    startRange = endRange - totalRangeButtons + 1;
  }

  const onPageClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.blur();
    const page = +e.currentTarget.dataset.page!;
    onChange(page);
  };
  const gotoNext = (e: MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.blur();
    onChange(page + 1);
  };
  const gotoPrev = (e: MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.blur();
    onChange(page - 1);
  };

  const getCurrentClass = (i: number) => (page === i ? "selected" : "");
  const rangeButtons = [];
  for (let i = startRange; i <= endRange; i++) {
    rangeButtons.push(
      <ACButtonGroupItem
        key={i}
        className={getCurrentClass(i)}
        onClick={onPageClick}
        data-page={i}
      >
        {i}
      </ACButtonGroupItem>,
    );
  }

  return (
    <div className="ac-pagination">
      {/* With property position, the browser captures the SS of the pagination properly */}
      <ACButtonGroup size={size} className="position-relative">
        <ACButtonGroupItem onClick={gotoPrev} disabled={page <= 1}>
          <ChevronLeftIcon />
        </ACButtonGroupItem>
        {showEdges && startRange > 1 ? (
          <ACButtonGroupItem
            className={getCurrentClass(1)}
            onClick={onPageClick}
            data-page={1}
          >
            1
          </ACButtonGroupItem>
        ) : null}
        {showEdges && startRange > 2 ? (
          <ACButtonGroupItem>...</ACButtonGroupItem>
        ) : null}
        {rangeButtons}
        {showEdges && endRange < totalPages - 1 ? (
          <ACButtonGroupItem>...</ACButtonGroupItem>
        ) : null}
        {showEdges && endRange < totalPages ? (
          <ACButtonGroupItem
            className={getCurrentClass(totalPages)}
            onClick={onPageClick}
            data-page={totalPages}
          >
            {totalPages}
          </ACButtonGroupItem>
        ) : null}
        <ACButtonGroupItem onClick={gotoNext} disabled={page >= totalPages}>
          <ChevronRightIcon />
        </ACButtonGroupItem>
      </ACButtonGroup>
    </div>
  );
}

export default React.memo(ACPagination);
